import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Button,
  Descriptions,
  DatePicker,
  Select,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { createLiabilityThunk, fetchLiabilitiesThunk, updateLiabilityThunk } from "../../../../Store/Slices/Finance/liabilities/liabilities.thunk";
import { clearSelectedLiability } from "../../../../Store/Slices/Finance/liabilities/liabilities.slice";
import { fetchCategory } from "../../../../Store/Slices/Finance/Category/financeCategory.Thunk";
import { fetchBudget } from "../../../../Store/Slices/Finance/budget/budget.thunk";
import { fetchEntity } from "../../../../Store/Slices/Finance/entitie/entity.thunk";
import toast from "react-hot-toast";

const { TextArea } = Input;
const { Option } = Select;

const LiabilityDrawer = ({ drawer, setDrawer }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [budgets, setBudgets] = useState([]);

  const { selectedLiability, loading } = useSelector(
    (state) => state.admin.liabilities
  );

  const categories =
    useSelector((state) => state.admin.financialCategory?.categories) || [];

  const { entities = [] } =
    useSelector((store) => store.admin.entity) || {};

  useEffect(() => {
    dispatch(
      fetchCategory({ categoryType: "expense", search: "", page: 1, limit: 10000 })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchEntity({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    const source = drawer.mode === "view" ? selectedLiability : drawer.record;

    const initialize = async () => {
      if ((drawer.mode === "edit" || drawer.mode === "view") && source) {
        const categoryId = source.categoryId?._id || source.categoryId;
        if (categoryId) {
          const res = await dispatch(fetchBudget({ categoryId, page: 1, limit: 10000 }));
          setBudgets(res?.payload?.data || []);
        }
        form.setFieldsValue({
          ...source,
          entityId: source.entityId?._id || source.entityId,
          categoryId,
          budgetId: source.budgetId?._id || source.budgetId,
          startDate: source.startDate ? dayjs(source.startDate) : null,
        });
      } else {
        form.resetFields();
        setBudgets([]);
      }
    };

    initialize();
  }, [drawer, selectedLiability, form, dispatch]);

  const handleCategoryChange = async (categoryId) => {
    form.setFieldsValue({ budgetId: undefined });
    const res = await dispatch(fetchBudget({ categoryId, page: 1, limit: 10000 }));
    setBudgets(res?.payload?.data || []);
  };

  const closeDrawer = () => {
    setDrawer({ open: false, mode: "add", record: null });
    dispatch(clearSelectedLiability());
    form.resetFields();
    setBudgets([]);
  };

  const onFinish = async (values) => {
    const selectedBudget = budgets.find((b) => b._id === values.budgetId);
    toast.dismiss()
    const payload = {
      ...values,
      budgetId: values.budgetId,
      subCategory: selectedBudget?.subCategory || "",
      startDate: values.startDate?.toISOString(),
    };

    try {
      if (drawer.mode === "edit" && drawer.record?._id) {
        await dispatch(
          updateLiabilityThunk({
            id: drawer.record._id,
            data: payload,
          })
        ).unwrap();

        toast.success("Liability updated successfully");
      } else {
        await dispatch(createLiabilityThunk(payload)).unwrap();

        toast.success("Liability created successfully");
      }
     await dispatch(fetchLiabilitiesThunk());
      closeDrawer();

    } catch (err) {
      toast.error(err || "Something went wrong");
    }
  };

  const isView = drawer.mode === "view";

  const labelCls = "text-xs font-bold uppercase tracking-widest text-gray-400";
  const valueCls = "text-sm font-semibold text-gray-800";

  return (
    < div className="flex flex-col gap-4">
      {isView ? (
        /* ── View Mode ──────────────────────────────── */
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className={`${labelCls} mb-4`}>Liability Overview</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {[
                { label: "Entity", value: selectedLiability?.entityId?.entityName },
                { label: "Category", value: selectedLiability?.categoryId?.categoryName },
                { label: "Sub Category", value: selectedLiability?.subCategory },
                { label: "Title", value: selectedLiability?.title },
                { label: "Interest Rate", value: selectedLiability?.interestRate ? `${selectedLiability.interestRate}%` : "—" },
                { label: "Interest Type", value: selectedLiability?.interestType },
                { label: "Tenure", value: selectedLiability?.tenureMonths ? `${selectedLiability.tenureMonths} Months` : "—" },
                { label: "Frequency", value: selectedLiability?.frequency },
                { label: "Start Date", value: selectedLiability?.startDate ? dayjs(selectedLiability.startDate).format("DD MMM YYYY") : "—" },
              ].map((row) => (
                <div key={row.label}>
                  <p className={labelCls}>{row.label}</p>
                  <p className={`${valueCls} mt-0.5 capitalize`}>{row.value || "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Financial summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className={`${labelCls} mb-4`}>Financial Summary</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                { label: "Principal Amount", value: selectedLiability?.principalAmount?.toLocaleString() },
                { label: "EMI Amount", value: selectedLiability?.emiAmount || 0 },
              ].map((row) => (
                <div key={row.label}>
                  <p className={labelCls}>{row.label}</p>
                  <p className={`${valueCls} mt-0.5`}>{row.value || "—"}</p>
                </div>
              ))}
            </div>
          </div>

          {selectedLiability?.description && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className={`${labelCls} mb-2`}>Description</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {selectedLiability.description}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* ── Add / Edit Mode ────────────────────────── */
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className={`${labelCls} mb-4`}>Liability Details</p>

            <Form form={form} layout="vertical" onFinish={onFinish}>

              <Form.Item
                name="entityId"
                label={<span className={labelCls}>Select Entity</span>}
                rules={[{ required: true, message: "Please select entity" }]}
              >
                <Select placeholder="Select entity" showSearch className="rounded-xl">
                  {entities.map((entity) => (
                    <Option key={entity._id} value={entity._id}>
                      {entity.entityName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="categoryId"
                label={<span className={labelCls}>Select Category</span>}
                rules={[{ required: true, message: "Please select category" }]}
              >
                <Select
                  placeholder="Select category"
                  showSearch
                  onChange={handleCategoryChange}
                  className="rounded-xl"
                >
                  {categories.map((cat) => (
                    <Option key={cat._id} value={cat._id}>
                      {cat.categoryName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={<span className={labelCls}>Sub Category</span>}
                name="budgetId"
                rules={[{ required: true, message: "Please select sub category" }]}
              >
                <Select placeholder="Select sub category" showSearch className="rounded-xl">
                  {budgets.map((budget) => (
                    <Option key={budget._id} value={budget._id}>
                      {budget.subCategory}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={<span className={labelCls}>Title</span>}
                name="title"
                rules={[{ required: true }]}
              >
                <Input className="rounded-xl" />
              </Form.Item>

              <Form.Item
                label={<span className={labelCls}>Description</span>}
                name="description"
              >
                <TextArea rows={3} className="rounded-xl" />
              </Form.Item>

              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item
                  label={<span className={labelCls}>Principal Amount</span>}
                  name="principalAmount"
                  rules={[{ required: true }]}
                >
                  <InputNumber className="w-full rounded-xl" min={0} />
                </Form.Item>

                <Form.Item
                  label={<span className={labelCls}>Interest Rate (%)</span>}
                  name="interestRate"
                  rules={[{ required: true }]}
                >
                  <InputNumber className="w-full rounded-xl" min={0} />
                </Form.Item>

                <Form.Item
                  label={<span className={labelCls}>Interest Type</span>}
                  name="interestType"
                  rules={[{ required: true }]}
                >
                  <Select className="rounded-xl">
                    <Option value="reducing">Reducing</Option>
                    <Option value="flat">Flat</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span className={labelCls}>Tenure (Months)</span>}
                  name="tenureMonths"
                  rules={[{ required: true }]}
                >
                  <InputNumber className="w-full rounded-xl" min={1} />
                </Form.Item>

                <Form.Item
                  label={<span className={labelCls}>Frequency</span>}
                  name="frequency"
                  rules={[{ required: true }]}
                >
                  <Select className="rounded-xl">
                    <Option value="Monthly">Monthly</Option>
                    <Option value="Quarterly">Quarterly</Option>
                    <Option value="Half yearly">Half yearly</Option>
                    <Option value="Yearly">Yearly</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<span className={labelCls}>Start Date</span>}
                  name="startDate"
                >
                  <DatePicker className="w-full rounded-xl" />
                </Form.Item>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-2.5 btn-gradient"
              >
                {loading
                  ? "Saving..."
                  : drawer.mode === "edit"
                    ? "Update Liability"
                    : "Create Liability"}
              </button>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiabilityDrawer;